<template>
  <b-card class="mpin-card">
    <div class="section-head">
      <h5>Transaction MPIN</h5>
      <span class="status-pill" :class="hasMpin ? 'is-success' : 'is-muted'">
        {{ hasMpin ? 'Active' : 'Not Set' }}
      </span>
    </div>

    <div v-if="isLoading" class="text-center py-4"><b-spinner></b-spinner></div>

    <template v-else>
      <p class="lead-text">
        Your MPIN is a 6-digit code used to authorise payments and withdrawals.
        Keep it private and never share it.
      </p>

      <div class="notice" :class="hasMpin ? 'done' : ''">
        <template v-if="hasMpin">Your MPIN is set and active on this account.</template>
        <template v-else>You have not set an MPIN yet. Set one to secure your transactions.</template>
      </div>

      <div class="mt-3">
        <b-button variant="primary" v-if="!hasMpin" @click="openSet(false)">Set MPIN</b-button>
        <template v-else>
          <b-button variant="primary" @click="openChange">Change MPIN</b-button>
          <b-button variant="secondary" class="ml-2" @click="openSet(true)">Forgot MPIN?</b-button>
          <b-button variant="secondary" class="ml-2" @click="openVerify">Verify MPIN</b-button>
        </template>
      </div>

      <p class="muted small mt-3" v-if="hasMpin">
        "Forgot MPIN?" resets it using your account password.
        "Change MPIN" uses your current MPIN.
      </p>
    </template>

    <!-- SET / RESET : authorised by the ACCOUNT PASSWORD -->
    <b-modal id="mpin-set-modal" centered :title="isReset ? 'Reset MPIN' : 'Set MPIN'">
      <p class="muted small">
        {{ isReset
          ? 'Confirm your account password to set a new MPIN.'
          : 'Choose a 6-digit MPIN and confirm it with your account password.' }}
      </p>
      <div class="form-row-x">
        <label>Account Password *</label>
        <b-form-input v-model="setForm.password" type="password" autocomplete="current-password"></b-form-input>
        <span class="text-danger" v-if="errors.password">{{ errors.password }}</span>
      </div>
      <div class="form-row-x">
        <label>New MPIN *</label>
        <b-form-input
          v-model.trim="setForm.mpin" type="password" maxlength="6" inputmode="numeric"
          placeholder="6 digits"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.mpin">{{ errors.mpin }}</span>
      </div>
      <div class="form-row-x">
        <label>Confirm MPIN *</label>
        <b-form-input
          v-model.trim="setForm.confirm_mpin" type="password" maxlength="6" inputmode="numeric"
          placeholder="Re-enter the 6 digits"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.confirm_mpin">{{ errors.confirm_mpin }}</span>
      </div>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="submitSet">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Saving..." : (isReset ? "Reset MPIN" : "Set MPIN") }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- CHANGE : authorised by the CURRENT MPIN -->
    <b-modal id="mpin-change-modal" centered title="Change MPIN">
      <p class="muted small">Enter your current MPIN, then choose a new one.</p>
      <div class="form-row-x">
        <label>Current MPIN *</label>
        <b-form-input
          v-model.trim="changeForm.old_mpin" type="password" maxlength="6" inputmode="numeric"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.old_mpin">{{ errors.old_mpin }}</span>
      </div>
      <div class="form-row-x">
        <label>New MPIN *</label>
        <b-form-input
          v-model.trim="changeForm.mpin" type="password" maxlength="6" inputmode="numeric"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.mpin">{{ errors.mpin }}</span>
      </div>
      <div class="form-row-x">
        <label>Confirm New MPIN *</label>
        <b-form-input
          v-model.trim="changeForm.confirm_mpin" type="password" maxlength="6" inputmode="numeric"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.confirm_mpin">{{ errors.confirm_mpin }}</span>
      </div>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="submitChange">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Saving..." : "Change MPIN" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- VERIFY : read-only check -->
    <b-modal id="mpin-verify-modal" centered title="Verify MPIN">
      <p class="muted small">Check your MPIN is correct. Nothing is changed.</p>
      <div class="form-row-x">
        <label>MPIN *</label>
        <b-form-input
          v-model.trim="verifyForm.mpin" type="password" maxlength="6" inputmode="numeric"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.verify_mpin">{{ errors.verify_mpin }}</span>
      </div>
      <div class="verify-result" :class="verifyResult.valid ? 'is-ok' : 'is-bad'" v-if="verifyResult.checked">
        {{ verifyResult.message }}
      </div>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="primary" :disabled="isSaving" @click="submitVerify">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Checking..." : "Verify" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Close</b-button>
      </template>
    </b-modal>

    <b-toast id="mpin-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
  </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";

const MPIN_LENGTH = 6;

export default {
  name: "MpinInfo",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isReset: false,
      infoText: "",
      formError: "",
      errors: {},
      setForm: { password: "", mpin: "", confirm_mpin: "" },
      changeForm: { old_mpin: "", mpin: "", confirm_mpin: "" },
      verifyForm: { mpin: "" },
      verifyResult: { checked: false, valid: false, message: "" },
    };
  },
  computed: {
    ...mapState(["mpinStatus"]),
    hasMpin: function () {
      return !!(this.mpinStatus && this.mpinStatus.has_mpin === true);
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...mapActions(["getMpinStatus", "setMpin", "changeMpin", "verifyMpin"]),
    load() {
      this.isLoading = true;
      const vm = this;
      return this.getMpinStatus()
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    reset() {
      this.errors = {};
      this.formError = "";
    },
    /** Rejects anything that is not exactly 6 digits, plus obvious weak PINs. */
    pinProblem(pin) {
      if (!pin) { return "MPIN is required."; }
      if (!/^[0-9]+$/.test(pin)) { return "MPIN must contain digits only."; }
      if (pin.length !== MPIN_LENGTH) { return "MPIN must be exactly " + MPIN_LENGTH + " digits."; }
      if (/^(\d)\1{5}$/.test(pin)) { return "MPIN cannot be the same digit repeated."; }
      if ("0123456789".indexOf(pin) !== -1 || "9876543210".indexOf(pin) !== -1) {
        return "MPIN cannot be a sequence of consecutive digits.";
      }
      return "";
    },

    openSet(isReset) {
      this.reset();
      this.isReset = !!isReset;
      this.setForm = { password: "", mpin: "", confirm_mpin: "" };
      this.$bvModal.show("mpin-set-modal");
    },
    submitSet() {
      this.reset();
      const e = {};
      if (!this.setForm.password) { e.password = "Account password is required."; }
      const p = this.pinProblem(this.setForm.mpin);
      if (p) { e.mpin = p; }
      if (!this.setForm.confirm_mpin) { e.confirm_mpin = "Please confirm your MPIN."; }
      else if (this.setForm.confirm_mpin !== this.setForm.mpin) { e.confirm_mpin = "The two MPINs do not match."; }
      this.errors = e;
      if (Object.keys(e).length) { return; }

      this.isSaving = true;
      const vm = this;
      return this.setMpin({
        password: this.setForm.password,
        mpin: this.setForm.mpin,
        confirm_mpin: this.setForm.confirm_mpin,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "MPIN set successfully.";
          vm.setForm = { password: "", mpin: "", confirm_mpin: "" };
          vm.$bvModal.hide("mpin-set-modal");
          vm.$bvToast.show("mpin-toast");
          return vm.load();
        }
        // 401 here means the account password was wrong, not the MPIN.
        if (res && res.passwordError) {
          vm.$set(vm.errors, "password", res.message || "That password is not correct.");
        }
        const fe = (res && res.fieldErrors) || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.formError = (res && res.message) || "Unable to set your MPIN.";
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },

    openChange() {
      this.reset();
      this.changeForm = { old_mpin: "", mpin: "", confirm_mpin: "" };
      this.$bvModal.show("mpin-change-modal");
    },
    submitChange() {
      this.reset();
      const e = {};
      if (!this.changeForm.old_mpin) { e.old_mpin = "Current MPIN is required."; }
      const p = this.pinProblem(this.changeForm.mpin);
      if (p) { e.mpin = p; }
      else if (this.changeForm.mpin === this.changeForm.old_mpin) {
        e.mpin = "The new MPIN must be different from the current one.";
      }
      if (!this.changeForm.confirm_mpin) { e.confirm_mpin = "Please confirm your new MPIN."; }
      else if (this.changeForm.confirm_mpin !== this.changeForm.mpin) { e.confirm_mpin = "The two MPINs do not match."; }
      this.errors = e;
      if (Object.keys(e).length) { return; }

      this.isSaving = true;
      const vm = this;
      return this.changeMpin({
        old_mpin: this.changeForm.old_mpin,
        mpin: this.changeForm.mpin,
        confirm_mpin: this.changeForm.confirm_mpin,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "MPIN changed successfully.";
          vm.changeForm = { old_mpin: "", mpin: "", confirm_mpin: "" };
          vm.$bvModal.hide("mpin-change-modal");
          vm.$bvToast.show("mpin-toast");
          return vm.load();
        }
        if (res && res.oldMpinError) {
          vm.$set(vm.errors, "old_mpin", res.message || "Your current MPIN is incorrect.");
        } else if (res && res.reuseError) {
          vm.$set(vm.errors, "mpin", res.message || "You cannot reuse a recent MPIN.");
        }
        const fe = (res && res.fieldErrors) || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.formError = (res && res.message) || "Unable to change your MPIN.";
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },

    openVerify() {
      this.reset();
      this.verifyForm = { mpin: "" };
      this.verifyResult = { checked: false, valid: false, message: "" };
      this.$bvModal.show("mpin-verify-modal");
    },
    submitVerify() {
      this.reset();
      this.verifyResult = { checked: false, valid: false, message: "" };
      const p = this.pinProblem(this.verifyForm.mpin);
      if (p) { this.$set(this.errors, "verify_mpin", p); return; }

      this.isSaving = true;
      const vm = this;
      return this.verifyMpin({ mpin: this.verifyForm.mpin }).then(function (res) {
        // A wrong PIN returns HTTP 200 with valid:false - check `valid`, not the status.
        if (res && res.ok) {
          vm.verifyResult = {
            checked: true,
            valid: !!res.valid,
            message: res.message || (res.valid ? "MPIN is correct." : "MPIN is incorrect."),
          };
          return;
        }
        if (res && res.notSet) {
          vm.formError = res.message || "No MPIN is set for this account.";
          return vm.load();
        }
        vm.formError = (res && res.message) || "Unable to verify your MPIN.";
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.mpin-card { min-height: 420px; }
.section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.section-head h5 { margin: 0; font-size: 16px; font-weight: 600; }
.status-pill { font-size: 11px; padding: 2px 10px; border-radius: 10px; font-weight: 700; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-muted { background: #eef1f4; color: #6c757d; }
.lead-text { font-size: 13px; color: #55606a; }
.notice {
  background: #eef1f4; border-radius: 8px; padding: 10px 12px;
  font-size: 13px; color: #55606a;
}
.notice.done { background: #e3f7e3; color: #007a00; }
.form-row-x { margin-bottom: 14px; }
.form-row-x label { display: block; font-size: 13px; color: #6c757d; margin-bottom: 4px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 3px; }
.muted { color: #6c757d; }
.small { font-size: 12px; }
.verify-result {
  border-radius: 8px; padding: 10px 12px; font-size: 13px; font-weight: 600; margin-top: 6px;
}
.verify-result.is-ok { background: #e3f7e3; color: #007a00; }
.verify-result.is-bad { background: #fde3e5; color: #b02a37; }
</style>
